import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT, useLanguage } from "../lib/LanguageContext";
import { formatDateTime } from "../lib/dateTime";
import { formatMoneyIntl } from "../lib/formatMoney";
import LoadFailure from "../components/ui/LoadFailure";
import TenantPicker from "./TenantPicker";
import DateRangeFilter, { isRangeInvalid } from "./DateRangeFilter";
import { LEGACY_SIMULATED, runStatus } from "./automation/runOutcome";
import { Loader2, UserPlus, CreditCard, Package, Workflow } from "lucide-react";

/**
 * Super admin -> Activity log (docs/auth-and-admin/super-admin-lists-contract.md section 7).
 *
 * The server sends STRUCTURED events, `{id, type, at, tenant, data}`, and no prose: this page
 * owns every word, through `act.*` keys in fr/en/es. The feed merges several collections, so
 * it is keyset-paged newest-first ("Load older" sends the last page's `next_cursor`) and has
 * no total.
 */

const PAGE_SIZE = 25;
const FAMILIES = ["tenant", "payment", "addon", "automation"];
const ICONS = { tenant: UserPlus, payment: CreditCard, addon: Package, automation: Workflow };
const NO_FILTERS = { type: "", tenant_id: "", from: "", to: "" };
const SELECT_CLASS = "bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700";

// The stored billing event type of an add-on line -> the sentence that says what happened to it.
// Any other type (a plain toggle) reads as enabled / disabled.
const BILLING_EVENT_SENTENCE = {
  booster_purchase: "purchased",
  booster_purchase_demo: "purchased",
  booster_cancel: "cancelled",
  addon_absorbed: "absorbed",
  booster_blocked_at_finalize: "blocked",
  booster_retired_by_plan_change: "retired",
};
// Automation run status (as automation/runOutcome.runStatus normalises it, legacy rows included)
// -> sentence. `succeeded` reads as "ran"; a legacy run whose actions were only simulated
// (`demo_mode`) says so, never that it ran.
const RUN_SENTENCE = {
  succeeded: "ran", running: "running", failed: "failed", partial: "partial", skipped: "skipped",
  [LEGACY_SIMULATED]: "simulatedLegacy",
};

const family = (type) => String(type || "").split(".")[0];

/**
 * The add-on's display name. The server names a booster from the catalogue; a module add-on
 * (`crm`, `ai_agent`, ...) has no catalogue row, so it arrives as its own code and is named
 * from the practitioner billing copy (`bill.addonName.<code>`), one wording for the same line
 * on both sides of the product. A code nothing names stays as it is.
 */
const addonName = (d, ta) => {
  if (d.addon_name && d.addon_name !== d.addon_code) return d.addon_name;
  return d.addon_code ? ta(`bill.addonName.${d.addon_code}`, undefined, d.addon_code) : "";
};

/** One event -> its sentence and its detail fragments. No server text is ever shown. */
function describe(ev, t, ta, lang) {
  const d = ev.data || {};
  switch (ev.type) {
    case "tenant.signup":
      return { title: t("act.ev.tenant.signup"), details: d.plan_id || d.plan_name ? [t("act.d.plan", { plan: d.plan_name || d.plan_id })] : [] };
    case "addon.enabled":
    case "addon.disabled": {
      const sentence = BILLING_EVENT_SENTENCE[d.billing_event_type] || ev.type.slice("addon.".length);
      const paid = d.amount_cents > 0;
      return {
        title: t(`act.ev.addon.${sentence}`, { addon: addonName(d, ta) }),
        details: paid ? [formatMoneyIntl(d.amount_cents, d.currency, lang)] : [],
      };
    }
    case "automation.run": {
      const trigger = d.trigger ? t(`act.trigger.${d.trigger}`, undefined, d.trigger) : null;
      return {
        // A dry run changed nothing, whatever status it ended with; it is never reported as a run.
        title: t(`act.ev.automation.${d.mode === "dry_run" ? "dryRun" : RUN_SENTENCE[runStatus(d)]}`, { rule: d.rule_name || "" }),
        details: trigger ? [t("act.d.trigger", { trigger })] : [],
      };
    }
    default:
      break;
  }
  if (family(ev.type) === "payment") {
    const status = ev.type.slice("payment.".length);
    const plan = d.plan_name || d.plan_id;
    // A completed row that captured nothing (the server's `zero_charge`, the same answer the
    // transaction ledger and the revenue figures use) is not a payment: it is named for what it
    // is, with no amount.
    if (d.zero_charge === "free_item" || d.zero_charge === "no_charge") {
      return {
        title: t(d.zero_charge === "free_item" ? "act.ev.freeItem" : "act.ev.noCharge"),
        details: plan ? [plan] : [],
      };
    }
    const amount = d.amount_cents == null ? "—" : formatMoneyIntl(d.amount_cents, d.currency, lang);
    return {
      title: t("act.ev.payment", { status: t(`tx.status.${status}`, undefined, status) }),
      details: [amount, ...(plan ? [plan] : [])],
    };
  }
  return { title: t("act.ev.unknown", { type: ev.type }), details: [] };
}

export default function ActivityLog() {
  const t = useT("superadmin");
  const ta = useT("admin");
  const { lang } = useLanguage();
  const [filters, setFilters] = useState(NO_FILTERS);
  const [items, setItems] = useState(null); // null until the first page of the current filters lands
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [olderFailed, setOlderFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // Bumped whenever the filters change or the feed is re-read. A response only lands when
  // the generation it started under is still current, so a slow page for the previous
  // filters (first page or "Load older") can never be appended to the newer feed.
  const generation = useRef(0);
  // The server refuses an inverted range, so it is never sent.
  const rangeInvalid = isRangeInvalid(filters.from, filters.to);

  const paramsFor = useCallback((cursor) => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    params.limit = PAGE_SIZE;
    if (cursor) params.cursor = cursor;
    return params;
  }, [filters]);

  // A 200 that is not the feed (the production proxy answers 200 + index.html for an unknown
  // /api path) is a failed read, not an empty feed.
  const readPage = (cursor) => api.get("/super-admin/activity", { params: paramsFor(cursor) }).then((r) => {
    if (!r.data || !Array.isArray(r.data.items)) throw new Error("unexpected activity payload");
    return r.data;
  });

  useEffect(() => {
    if (rangeInvalid) return undefined;
    const mine = ++generation.current;
    setItems(null);
    setNextCursor(null);
    setLoadingMore(false);
    setFailed(false);
    setOlderFailed(false);
    readPage(null)
      .then((page) => {
        if (mine !== generation.current) return;
        setItems(page.items);
        setNextCursor(page.next_cursor || null);
      })
      .catch(() => { if (mine === generation.current) setFailed(true); });
    return () => { generation.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, attempt, rangeInvalid]);

  const loadOlder = () => {
    const mine = generation.current;
    setLoadingMore(true);
    setOlderFailed(false);
    readPage(nextCursor)
      .then((page) => {
        if (mine !== generation.current) return;
        setItems((cur) => [...cur, ...page.items]);
        setNextCursor(page.next_cursor || null);
      })
      .catch(() => { if (mine === generation.current) setOlderFailed(true); })
      .finally(() => { if (mine === generation.current) setLoadingMore(false); });
  };

  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <div data-testid="sa-activity">
      <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("act.title")}</h1>
      <p className="text-slate-500 mb-6">{t("act.subtitle")}</p>

      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="act-filters">
        <select
          value={filters.type}
          onChange={(e) => setFilter("type")(e.target.value)}
          className={SELECT_CLASS}
          aria-label={t("act.filterType")}
          data-testid="act-filter-type"
        >
          <option value="">{t("act.typeAll")}</option>
          {FAMILIES.map((f) => <option key={f} value={f}>{t(`act.type.${f}`)}</option>)}
        </select>
        <TenantPicker
          value={filters.tenant_id}
          onChange={(id) => setFilter("tenant_id")(id)}
          allowAll
          className="w-64"
          testId="act-filter-tenant"
        />
        <DateRangeFilter
          from={filters.from}
          to={filters.to}
          onFromChange={setFilter("from")}
          onToChange={setFilter("to")}
          testIds={{ from: "act-filter-from", to: "act-filter-to", error: "act-range-error" }}
        />
      </div>

      <LoadFailure
        message={failed && !rangeInvalid ? t("act.loadFailed") : ""}
        onRetry={() => setAttempt((n) => n + 1)}
        retryLabel={t("sal.retry")}
        testId="act-load-error"
        className="mb-4"
      />

      {!rangeInvalid && !failed && items === null && <Loader2 className="w-6 h-6 animate-spin text-amber-600" />}

      {!rangeInvalid && items !== null && (
        <>
          <div className="space-y-2">
            {items.map((ev) => {
              const Icon = ICONS[family(ev.type)] || UserPlus;
              const { title, details } = describe(ev, t, ta, lang);
              const who = ev.tenant ? (ev.tenant.name || ev.tenant.slug || ev.tenant.tenant_id) : null;
              return (
                <div key={ev.id} className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl p-4" data-testid={`act-row-${ev.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {who && (
                        <Link
                          to={`/super-admin/tenants/${ev.tenant.tenant_id}`}
                          className="text-amber-700 hover:underline"
                          data-testid={`act-tenant-${ev.id}`}
                        >
                          {who}
                        </Link>
                      )}
                      {who && details.length > 0 && " · "}
                      {details.join(" · ")}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 whitespace-nowrap" data-testid={`act-at-${ev.id}`}>
                    {formatDateTime(ev.at, lang)}
                  </div>
                </div>
              );
            })}
            {items.length === 0 && !failed && (
              <div className="px-6 py-10 text-center text-slate-400 text-sm">{t("act.empty")}</div>
            )}
          </div>

          {olderFailed && (
            <p role="alert" className="text-sm text-rose-600 mt-3" data-testid="act-load-error">{t("act.loadFailed")}</p>
          )}
          {nextCursor && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={loadOlder}
                disabled={loadingMore}
                className="text-sm px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                data-testid="act-load-older"
              >
                {loadingMore ? t("act.loadingOlder") : t("act.loadOlder")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
